import type { ConnectionEndpoint } from "./ConnectionEndpointProvider";
import type { AnalyserConnection } from "./VirtualConnections";
import {
  DEFAULT_CONNECTION_ROUTE_RULES,
  type ConnectionRouteKind,
  type ConnectionRouteRule,
} from "./connectionRouteRules";

export type SelectedConnectionRoute = {
  connectionId: string;
  start: ConnectionEndpoint;
  end: ConnectionEndpoint;
  ruleId: string;
};
type EndpointPair = { start: ConnectionEndpoint[]; end: ConnectionEndpoint[] };

/** Classifies actual model endpoints, independently of the ancestor used as a visible anchor. */
function routeKind(connection: AnalyserConnection): { kind: ConnectionRouteKind; deviceIsEnd: boolean } {
  const from = connection.fromDevice,
    to = connection.toDevice;
  const fromConsumer = from?.className === "Consumer",
    toConsumer = to?.className === "Consumer";
  if (fromConsumer && toConsumer)
    return { kind: connection.type === "TerminalVia" ? "terminal-via" : "other", deviceIsEnd: false };
  const fromScene = from?.isScene || from?.isMultiplexer;
  const toScene = to?.isScene || to?.isMultiplexer;
  const scenePartner = fromScene ? to : toScene ? from : undefined;
  if (scenePartner?.className === "Button" || scenePartner?.isButtonChannel) {
    return { kind: "button-scene", deviceIsEnd: !!fromScene };
  }
  const partner = fromConsumer ? to : toConsumer ? from : undefined;
  let kind: ConnectionRouteKind = "other";
  if (partner?.isActorChannel) kind = "consumer-actor";
  else if (partner?.isButtonChannel) kind = "consumer-button";
  else if (partner?.isScene || partner?.isMultiplexer) kind = "consumer-scene";
  return { kind, deviceIsEnd: toConsumer };
}

/** Keeps only finite points grouped by their real Connection role; never connects start to start. */
function groupEndpoints(endpoints: readonly ConnectionEndpoint[]): Map<string, EndpointPair> {
  const groups = new Map<string, EndpointPair>();
  for (const endpoint of endpoints) {
    if (!Number.isFinite(endpoint.point?.clientX) || !Number.isFinite(endpoint.point?.clientY)) continue;
    if (endpoint.role !== "start" && endpoint.role !== "end") continue;
    let group = groups.get(endpoint.connectionId);
    if (!group) {
      group = { start: [], end: [] };
      groups.set(endpoint.connectionId, group);
    }
    group[endpoint.role].push(endpoint);
  }
  return groups;
}

/** Stable provider keys decide ties; coordinates are used only for keyless third-party endpoints. */
function pairKey(start: ConnectionEndpoint, end: ConnectionEndpoint): string {
  return JSON.stringify([
    start.key ?? [start.point.clientX, start.point.clientY],
    end.key ?? [end.point.clientX, end.point.clientY],
  ]);
}

/** Picks one route per Connection using ordered rules, while preserving the model's start/end direction. */
export function selectConnectionRoutes(
  endpoints: readonly ConnectionEndpoint[],
  connections: readonly AnalyserConnection[],
  rules: readonly ConnectionRouteRule[] = DEFAULT_CONNECTION_ROUTE_RULES,
): SelectedConnectionRoute[] {
  const groups = groupEndpoints(endpoints);
  const routes: SelectedConnectionRoute[] = [];
  const seen = new Set<string>();
  for (const connection of connections) {
    if (seen.has(connection.id)) continue;
    seen.add(connection.id);
    const from = connection.fromDevice,
      to = connection.toDevice;
    if (connection.isDeleted || !from || !to || from.isDeleted || to.isDeleted || from.id === to.id) continue;
    const group = groups.get(connection.id);
    if (!group) continue;
    const { kind, deviceIsEnd } = routeKind(connection);
    for (const rule of rules) {
      if (rule.kind !== "any" && rule.kind !== kind) continue;
      let selected: SelectedConnectionRoute | undefined;
      let selectedKey = "";
      for (const originalStart of group.start)
        for (const originalEnd of group.end) {
          const sameColumn = originalStart.surface === "connection-dashboard" && originalEnd.surface === "connection-dashboard"
            && originalStart.providerId && originalStart.providerId === originalEnd.providerId;
          const followScroll = sameColumn && originalStart.scrollGeometry && originalEnd.scrollGeometry;
          const start = followScroll ? { ...originalStart, point: originalStart.scrollGeometry!.point,
            exitDirection: originalStart.scrollGeometry!.exitDirection, isScrollClipped: false } : originalStart;
          const end = followScroll ? { ...originalEnd, point: originalEnd.scrollGeometry!.point,
            exitDirection: originalEnd.scrollGeometry!.exitDirection, isScrollClipped: false } : originalEnd;
          const first = deviceIsEnd ? end : start,
            second = deviceIsEnd ? start : end;
          if (first.surface !== rule.first || second.surface !== rule.second) continue;
          if (rule.secondObjectClass && second.representedObjectClass !== rule.secondObjectClass) continue;
          // Hidden descendants sharing one row in one dashboard column have no visible route.
          // Compare row identity, since opposite exit directions can give that row different coordinates.
          if (start.surface === "connection-dashboard" && end.surface === "connection-dashboard"
            && start.providerId && start.providerId === end.providerId
            && (start.isScrollClipped || end.isScrollClipped
              || (start.anchorObjectId && start.anchorObjectId === end.anchorObjectId))) continue;
          if (start.point.clientX === end.point.clientX && start.point.clientY === end.point.clientY) continue;
          const key = pairKey(start, end);
          if (!selected || key < selectedKey) {
            selectedKey = key;
            selected = { connectionId: connection.id, start, end, ruleId: rule.id };
          }
        }
      if (selected) {
        routes.push(selected);
        break;
      }
    }
  }
  return routes;
}
